import * as React from 'react';
import { Root } from '../Root/Root';
import * as uuid from 'uuid/v4';
import * as css from './DealerViewer.scss';
import { create as fedopsLoggerFactory } from '@wix/fedops-logger';
import {
  DealerViewerProps,
  viewerDefaultProps,
  viewerPropTypes,
} from '../../interfaces';
import { CmsDataStore } from '@wix/dealer-store/dist/src';
import { BiLogger } from '@wix/dealer-common/dist/src/services/BiLogger';
import { convertOfferToBannerData } from '@wix/dealer-common/dist/src/converters/toDomain';
import * as _get from 'lodash/get';
import {
  BANNER_INIT,
  BANNER_RENDERED,
  VIEW_EVENT,
} from '@wix/dealer-common/dist/src/services/BiLogger/bi-events';
import {
  calculateHeight,
  calculateWidth,
} from '@wix/dealer-common/dist/src/services/DimensionCalculator';
import {
  CmsStoreInitData,
  OnClickParams,
  OnViewParams,
} from '@wix/dealer-common/dist/src/interfaces';
import i18n from '../../i18n';
import { I18nextProvider } from 'react-i18next';
import { BannerData } from '@wix/dealer-common/dist/src/interfaces/BannerData';

const { viewerUrl, ...dealerStaticViewerPropTypes } = viewerPropTypes;
export type DealerStaticViewerProps = Pick<
  DealerViewerProps,
  Exclude<keyof DealerViewerProps, 'viewerUrl'>
>;
export class DealerStaticViewerComp extends React.Component<
  DealerStaticViewerProps
> {
  static propTypes = { ...dealerStaticViewerPropTypes };
  static defaultProps = { ...viewerDefaultProps };
  private readonly requestGuid: string;
  private readonly cmsDataStore: CmsDataStore;
  private readonly biLogger: BiLogger;
  private hasError = false;

  constructor(props: DealerViewerProps) {
    super(props);
    const fedopsLogger = fedopsLoggerFactory('react-dealer-viewer');
    this.biLogger = new BiLogger(props.additionalBiParams);

    let banner: BannerData;
    if (this.props.offer) {
      banner = convertOfferToBannerData(this.props.offer);
    } else {
      banner = this.props.banner;
    }

    const banners: BannerData = {
      ...banner,
      bannerTemplate: JSON.parse(banner.bannerTemplate),
    };

    const data: CmsStoreInitData = {
      bannerPosition: this.props.bannerPosition,
      locale: this.props.locale,
      metasiteGuid: this.getMetasiteGuid(this.props),
      onDisplay: this.props.onDisplay,
      onEmpty: this.props.onEmpty,
      onError: this.props.onError,
      translationsUrl: this.props.translationsUrl,
      fedopsLogger,
      signedInstance: this.props.signedInstance,
      banners,
      biLogger: this.biLogger,
    };
    this.cmsDataStore = new CmsDataStore(data);
    this.requestGuid = uuid();
    this.log(
      BANNER_INIT,
      { requestGuid: this.requestGuid },
      { newEventStyle: true },
    );
  }

  private readonly getMetasiteGuid = (
    props: DealerStaticViewerProps,
  ): string => {
    const metaSiteId = _get(
      props,
      'dealerOptions.metaSiteId',
      props.metaSiteId,
    );
    const metasiteGuid = _get(
      props,
      'dealerOptions.metasiteGuid',
      props.metasiteGuid,
    );
    return metaSiteId || metasiteGuid;
  };

  log = (
    eventId: number,
    additionalParams: { [key: string]: any } = {},
    { newEventStyle = true } = {},
  ) => {
    return this.biLogger.log(
      eventId,
      newEventStyle,
      additionalParams,
      this.cmsDataStore.getLocale(),
      this.cmsDataStore.getMetaSiteId(),
      this.cmsDataStore.getBannerRoot(),
      this.cmsDataStore.getBannerPosition(),
    );
  };

  async componentDidMount() {
    this.log(
      BANNER_RENDERED,
      { requestGuid: this.requestGuid },
      { newEventStyle: true },
    );

    this.props.onDisplay &&
      this.props.onDisplay({
        isDisplayed: this.cmsDataStore.shouldShowBanner(),
        bannerLocationOnPage: this.cmsDataStore.getBannerLocation(),
        width: calculateWidth(
          this.cmsDataStore.pageSettings,
          this.cmsDataStore.getContext(),
        ),
        height: calculateHeight(
          this.cmsDataStore.pageSettings,
          this.cmsDataStore.getContext(),
        ),
      });
  }

  onDealerError = () => {
    this.hasError = true;
    this.forceUpdate();
    if (this.props.onError) {
      this.props.onError();
    }
  };

  getOfferId = () => {
    const banner = this.cmsDataStore.getBannerRoot();
    const offerId = banner && banner.lightboxGuid;
    return offerId;
  };

  createOnDealerView = (onView: Function) => (aboveTheFold: boolean) => {
    this.logBiView(aboveTheFold);
    if (onView) {
      onView({ offerId: this.getOfferId() } as OnViewParams);
    }
  };

  createOnDealerClick = (onClick: Function) => (params: OnClickParams) => {
    if (onClick) {
      onClick({ ...params, offerId: this.getOfferId() });
    }
  };

  logBiView = (aboveTheFold: boolean) =>
    this.log(VIEW_EVENT, { aboveTheFold }, { newEventStyle: false });

  render() {
    const onDealerView = this.createOnDealerView(this.props.onView);
    const onDealerClick = this.createOnDealerClick(this.props.onClick);
    return this.hasError ? (
      <span className={css.failedContainer} />
    ) : (
      <span className={css.dealerViewer}>
        <Root
          dealerOptions={this.props.dealerOptions}
          cmsDataStore={this.cmsDataStore}
          loaded={true}
          onView={onDealerView}
          onClick={onDealerClick}
          onError={this.onDealerError}
        />
      </span>
    );
  }
}

export class DealerStaticViewer extends React.Component<
  DealerStaticViewerProps
> {
  private static i18n: any;
  static getI18n(locale: string, translationsUrl: string) {
    if (DealerStaticViewer.i18n === undefined) {
      DealerStaticViewer.i18n = i18n({ locale, baseUrl: translationsUrl });
    }

    return DealerStaticViewer.i18n;
  }

  render() {
    return (
      <I18nextProvider
        i18n={DealerStaticViewer.getI18n(
          this.props.locale,
          this.props.translationsUrl,
        )}
      >
        <DealerStaticViewerComp {...this.props} />
      </I18nextProvider>
    );
  }
}
